import { useEffect, useState } from "react";
import { Outlet, useNavigate, useParams, Link } from "react-router-dom";
import { useAuth } from "@/features/auth/context/AuthContext";
import { LmsApiService, Module, Lesson, EnrollmentProgress } from "../backend";
import { cn } from "@/lib/utils";
import { Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loading } from "@/components/ui/loading";

const lmsService = new LmsApiService();
const COURSE_ID = 1; // Hardcoded for this MVP as per requirements (German Nursing B1)

export default function LmsLayout() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const { lessonId } = useParams();

  const [modules, setModules] = useState<Module[]>([]);
  const [lessonsMap, setLessonsMap] = useState<Record<number, Lesson[]>>({});
  const [enrollment, setEnrollment] = useState<
    EnrollmentProgress | undefined
  >();
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!profile?.id) return;

      try {
        const { modules: fetchedModules, enrollment: fetchedEnrollment } =
          await lmsService.getModules(profile.id, COURSE_ID);
        setModules(fetchedModules);
        setEnrollment(fetchedEnrollment);

        // Fetch lessons for all modules
        const lessonsData: Record<number, Lesson[]> = {};
        await Promise.all(
          fetchedModules.map(async (mod) => {
            const lessons = await lmsService.getLessons(mod.id);
            lessonsData[mod.id] = lessons;
          })
        );
        setLessonsMap(lessonsData);
      } catch (error) {
        console.error("Failed to fetch LMS data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [profile?.id]);

  // Helper to check if a lesson is completed (has submissions or is visited)
  // For this MVP, we'll consider "visited" as enough to unlock next, but "completed"
  // might require exercises. The backend updates `last_visited_lesson_id`.
  // We can visualize "completed" if it's before the current last visited, or if we had explicit status.
  // For now, let's just show navigation.

  if (loading) return <Loading />;

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <div
        className={cn(
          "flex flex-col border-r bg-white transition-all duration-300",
          sidebarOpen ? "w-80" : "w-0 overflow-hidden"
        )}
      >
        <div className="flex items-center justify-between p-4">
          <h2 className="font-semibold text-lg text-[#471999]">
            Course Content
          </h2>
        </div>

        <ScrollArea className="flex-1">
          <div className="p-4 space-y-6">
            {modules.map((module) => (
              <div key={module.id} className="space-y-2">
                <h3 className="font-medium text-sm text-gray-500 uppercase tracking-wider">
                  {module.title}
                </h3>
                <div className="space-y-1">
                  {lessonsMap[module.id]?.map((lesson) => {
                    const isActive = Number(lessonId) === lesson.id;
                    const isCompleted =
                      enrollment?.lessonsProgress?.[lesson.id]?.completed;
                    // Simple logic: unlock if it's the first lesson OR previous lesson was visited.
                    // For MVP, we unlock everything to allow easy testing as requested.
                    const isLocked = false;

                    return (
                      <Link
                        key={lesson.id}
                        to={
                          isLocked
                            ? "#"
                            : `/job-recognition-course/lesson/${lesson.id}`
                        }
                        className={cn(
                          "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
                          isActive
                            ? "bg-[#f5f2ff] text-[#471999] font-medium"
                            : "text-gray-700 hover:bg-gray-100",
                          isLocked &&
                            "opacity-50 cursor-not-allowed hover:bg-transparent"
                        )}
                      >
                        {isActive ? (
                          <div className="w-2 h-2 rounded-full bg-[#471999]" />
                        ) : isCompleted ? (
                          <div className="w-2 h-2 rounded-full bg-green-500" />
                        ) : (
                          <div className="w-2 h-2 rounded-full bg-gray-300" />
                        )}
                        <span className="flex-1 truncate">{lesson.title}</span>
                        {isLocked && <Lock className="w-3 h-3 text-gray-400" />}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>

        <div className="p-4 border-t bg-gray-50">
          <Button
            variant="outline"
            className="w-full"
            onClick={() => navigate("/job-recognition-course")}
          >
            Back to Overview
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="h-16 border-b bg-white flex items-center px-6 justify-between shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? "Close Menu" : "Open Menu"}
          </Button>
          <div className="flex items-center gap-4">
            {/* Add user progress summary here if needed */}
          </div>
        </header>

        <main className="flex-1 overflow-auto p-6 md:p-10">
          <div className="max-w-4xl mx-auto">
            <Outlet context={{ enrollment, setEnrollment }} />
          </div>
        </main>
      </div>
    </div>
  );
}
